'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const { language, t } = useLanguage();

  return (
    <section className={`relative bg-gradient-to-br from-primary via-secondary to-brand-ocean text-white py-20 lg:py-32 overflow-hidden ${className}`}>
      {/* Lighthouse Beam Effect */}
      <div className="absolute inset-0 lighthouse-beam opacity-20"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Lighthouse Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 lg:w-24 lg:h-24 bg-white/10 backdrop-blur-sm rounded-full mb-6 trust-glow">
            <svg
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-12 lg:w-14 lg:h-14"
            >
              <circle cx="24" cy="24" r="22" fill="var(--color-accent)" />
              <path
                d="M24 8L24 28M24 28L16 20M24 28L32 20"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="24" cy="32" r="3" fill="white" className="animate-pulse" />
              <path
                d="M12 38C12 38 16 34 24 34C32 34 36 38 36 38"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <h1 className="text-4xl lg:text-6xl font-headline font-bold mb-6 leading-tight">
            {t({ en: 'Your Trusted Moving Partner in Kuwait', ar: 'شريكك الموثوق للنقل في الكويت' })}
          </h1>
          
          <p className="text-xl lg:text-2xl font-body mb-8 text-white/90 leading-relaxed">
            {t({ en: 'Almanara (المنارة) - A Beacon of Trust Through Life\'s Transitions', ar: 'المنارة - منارة الثقة في رحلات الحياة' })}
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-4 text-base lg:text-lg">
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Icon name="ClockIcon" size={20} variant="solid" />
              <span className="font-body font-medium">{t({ en: '24/7 Available', ar: 'متاح على مدار الساعة' })}</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Icon name="ShieldCheckIcon" size={20} variant="solid" />
              <span className="font-body font-medium">{t({ en: 'Fully Insured', ar: 'مؤمن بالكامل' })}</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
              <Icon name="StarIcon" size={20} variant="solid" />
              <span className="font-body font-medium">{t({ en: '5-Star Service', ar: 'خدمة 5 نجوم' })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="var(--color-background)"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;